import React, { useCallback, useState } from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Chip,
  Skeleton,
  Alert,
  Stack,
  IconButton,
} from '@mui/material';
import { Biotech, ChevronLeft, ChevronRight } from '@mui/icons-material';
import { useListaPermitida } from '../../../authorization/useListaPermitida';
import { examApplicationService } from '../../../services/examApplicationService';
import type { ExamWithApplication } from '../../../types/examApplication';
import {
  ExpirationStatus,
  ExpirationStatusLabels,
  ExpirationStatusColors,
  getExpirationStatus,
  formatDaysUntilExpiration,
  ApplicationLevelLabels,
} from '../../../types/examApplication';

interface EmployeeExamsCardProps {
  employeeId: number;
  companyId: number;
  departmentId: number;
  occupationId: number;
}

const ACCENT = '#1565C0';
const PAGE_SIZE = 5;

export const EmployeeExamsCard: React.FC<EmployeeExamsCardProps> = ({
  employeeId,
  companyId,
  departmentId,
  occupationId,
}) => {
  const [page, setPage] = useState(0);

  // Aplicação de exame tem permissão própria: os quatro recortes vinham com `catch` vazio, e
  // o cartão ficava vazio e mudo para quem não lê aplicação de exame.
  const carregar = useCallback(async () => {
    const [comp, dept, occ, emp] = await Promise.all([
      examApplicationService.getForCompany(companyId),
      examApplicationService.getForDepartment(departmentId),
      examApplicationService.getForOccupation(occupationId),
      examApplicationService.getForEmployee(employeeId),
    ]);
    const todos = [
      ...(comp.applied ?? []),
      ...(dept.applied ?? []),
      ...(occ.applied ?? []),
      ...(emp.applied ?? []),
    ];
    // Vencido primeiro, depois o que vence logo: é a ordem em que se age sobre a lista.
    const urgencia: Record<string, number> = {
      [ExpirationStatus.EXPIRED]: 0,
      [ExpirationStatus.EXPIRING_SOON]: 1,
      [ExpirationStatus.OK]: 2,
    };
    return todos.sort(
      (um, outro) => urgencia[getExpirationStatus(um)] - urgencia[getExpirationStatus(outro)]
    );
  }, [companyId, departmentId, occupationId, employeeId]);

  const lista = useListaPermitida<ExamWithApplication>(
    'exam_application.read',
    'Exames aplicados',
    carregar
  );
  const exams = lista.itens;
  const loading = lista.carregando;

  const countByStatus = exams.reduce<Record<string, number>>((acc, ex) => {
    const s = getExpirationStatus(ex);
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});

  const totalPages = Math.ceil(exams.length / PAGE_SIZE);
  const paged = exams.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  return (
    <Card variant="outlined" sx={{ height: '100%', borderTop: `3px solid ${ACCENT}` }}>
      <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
        {/* Header */}
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <Box sx={{ color: ACCENT, display: 'flex' }}><Biotech fontSize="small" /></Box>
          <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>
            Exames Ocupacionais
          </Typography>
          {!loading && !lista.erro && (
            <Chip label={exams.length} size="small" sx={{ bgcolor: ACCENT, color: '#fff', fontWeight: 700 }} />
          )}
        </Box>

        {/* Body */}
        {loading && (
          <Stack spacing={1}>
            {[0, 1, 2].map((i) => <Skeleton key={i} variant="rounded" height={32} />)}
          </Stack>
        )}

        {lista.erro && (
          <Alert severity="error" variant="outlined" sx={{ py: 0.5 }}>{lista.erro}</Alert>
        )}

        {!loading && !lista.erro && exams.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            {lista.bloqueio ?? 'Nenhum registro encontrado'}
          </Typography>
        )}

        {!loading && !lista.erro && exams.length > 0 && (
          <>
            {/* Summary chips */}
            <Box display="flex" gap={0.5} flexWrap="wrap" mb={1.5}>
              {([ExpirationStatus.OK, ExpirationStatus.EXPIRING_SOON, ExpirationStatus.EXPIRED] as const).map((s) =>
                countByStatus[s] ? (
                  <Chip
                    key={s}
                    label={`${ExpirationStatusLabels[s]}: ${countByStatus[s]}`}
                    size="small"
                    color={ExpirationStatusColors[s]}
                    variant="outlined"
                  />
                ) : null
              )}
            </Box>

            {/* List */}
            <Stack spacing={0.75}>
              {paged.map((ex) => {
                const status = getExpirationStatus(ex);
                return (
                  <Box
                    key={`${ex.applicationLevel}-${ex.id}`}
                    display="flex"
                    alignItems="center"
                    gap={0.5}
                    sx={{ py: 0.5, px: 1, borderRadius: 1, bgcolor: 'action.hover' }}
                  >
                    <Chip
                      label={ApplicationLevelLabels[ex.applicationLevel]}
                      size="small"
                      variant="outlined"
                      sx={{ height: 18, fontSize: '0.6rem', minWidth: 56 }}
                    />
                    <Typography variant="caption" sx={{ flex: 1 }} noWrap title={ex.examName}>
                      {ex.examName}
                    </Typography>
                    <Chip
                      label={formatDaysUntilExpiration(ex.daysUntilExpiration)}
                      size="small"
                      color={ExpirationStatusColors[status]}
                      sx={{ height: 20, fontSize: '0.7rem' }}
                    />
                  </Box>
                );
              })}
            </Stack>

            {/* Pagination */}
            {totalPages > 1 && (
              <Box display="flex" alignItems="center" justifyContent="flex-end" gap={0.5} mt={1}>
                <Typography variant="caption" color="text.secondary">
                  {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, exams.length)} de {exams.length}
                </Typography>
                <IconButton size="small" disabled={page === 0} onClick={() => setPage(page - 1)}>
                  <ChevronLeft fontSize="small" />
                </IconButton>
                <IconButton size="small" disabled={page >= totalPages - 1} onClick={() => setPage(page + 1)}>
                  <ChevronRight fontSize="small" />
                </IconButton>
              </Box>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};
